import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AppLayout from "./components/AppLayout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Clients from "./pages/Clients";
import Quotes from "./pages/Quotes";
import QuoteEditor from "./pages/QuoteEditor";
import QuotePrint from "./pages/QuotePrint";
import Planning from "./pages/Planning";
import Fleet from "./pages/Fleet";
import Invoices from "./pages/Invoices";
import Assistant from "./pages/Assistant";
import SettingsPage from "./pages/Settings";
import Team from "./pages/Team";
import PublicQuote from "./pages/PublicQuote";
import AcceptInvite from "./pages/AcceptInvite";
import RemoteSign from "./pages/RemoteSign";
import PublicInvoice from "./pages/PublicInvoice";
import { can } from "./lib/labels";
import NomadeLayout from "./pages/nomade/NomadeLayout";
import NomadePlanning from "./pages/nomade/NomadePlanning";
import NomadeMove from "./pages/nomade/NomadeMove";
import NomadeExpenses from "./pages/nomade/NomadeExpenses";
import NomadeItinerary from "./pages/nomade/NomadeItinerary";
import Subscription from "./pages/Subscription";
import PaymentSuccess from "./pages/PaymentSuccess";
import PublicReview from "./pages/PublicReview";
import Reviews from "./pages/Reviews";

const homeFor = (user) => (can(user, "dashboard") ? "/app" : can(user, "field") ? "/nomade" : "/");

function Allow({ perm, children }) {
  const { user } = useAuth();
  return can(user, perm) ? children : <Navigate to={homeFor(user)} replace />;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#1559d6]" />
      </div>
    );
  }
  return user ? children : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/inscription" element={<Register />} />
          <Route path="/devis-client/:token" element={<PublicQuote />} />
          <Route path="/invitation/:token" element={<AcceptInvite />} />
          <Route path="/signature/:token" element={<RemoteSign />} />
          <Route path="/facture-client/:token" element={<PublicInvoice />} />
          <Route path="/avis/:token" element={<PublicReview />} />
          <Route path="/paiement/succes" element={<Protected><PaymentSuccess /></Protected>} />
          <Route path="/app/devis/:id/imprimer" element={<Protected><QuotePrint /></Protected>} />
          <Route path="/app" element={<Protected><AppLayout /></Protected>}>
            <Route index element={<Allow perm="dashboard"><Dashboard /></Allow>} />
            <Route path="clients" element={<Allow perm="clients"><Clients /></Allow>} />
            <Route path="avis" element={<Allow perm="clients"><Reviews /></Allow>} />
            <Route path="devis" element={<Allow perm="quotes"><Quotes /></Allow>} />
            <Route path="devis/nouveau" element={<Allow perm="quotes"><QuoteEditor /></Allow>} />
            <Route path="devis/:id" element={<Allow perm="quotes"><QuoteEditor /></Allow>} />
            <Route path="planning" element={<Allow perm="moves"><Planning /></Allow>} />
            <Route path="flotte" element={<Allow perm="fleet"><Fleet /></Allow>} />
            <Route path="factures" element={<Allow perm="invoices"><Invoices /></Allow>} />
            <Route path="assistant" element={<Allow perm="ai"><Assistant /></Allow>} />
            <Route path="equipe" element={<Allow perm="team"><Team /></Allow>} />
            <Route path="parametres" element={<Allow perm="settings"><SettingsPage /></Allow>} />
            <Route path="abonnement" element={<Allow perm="billing"><Subscription /></Allow>} />
          </Route>
          <Route path="/nomade" element={<Protected><Allow perm="field"><NomadeLayout /></Allow></Protected>}>
            <Route index element={<NomadePlanning />} />
            <Route path="chantier/:id" element={<NomadeMove />} />
            <Route path="depenses" element={<NomadeExpenses />} />
            <Route path="itineraire" element={<NomadeItinerary />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
